import { notifyError } from "@/lib/notify-error";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { Building2, Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { PageHeader } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { DisabledReasonTooltip } from "@/components/ui/disabled-reason-tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAppNavigate } from "@/lib/use-app-navigate";

export const Route = createFileRoute("/admin/organizations")({
  component: OrganizationsPage,
});

type Organization = {
  _id: Id<"organizations">;
  name: string;
  isHomeOrganization: boolean;
};

function OrganizationsPage() {
  const navigate = useAppNavigate();
  const { data, isPending, error } = useQuery(
    convexQuery(api.admin.organizations.listForEmulation, {})
  );
  const startEmulation = useMutation(api.admin.organizations.startEmulation);
  const [pendingOrganizationId, setPendingOrganizationId] = useState<Id<"organizations"> | null>(null);
  const organizations = data ?? [];

  const handleEnter = async (organization: Organization) => {
    setPendingOrganizationId(organization._id);
    try {
      await startEmulation({ organizationId: organization._id });
      toast.success(`Now assisting ${organization.name}`);
      navigate({ to: "/", title: organization.name });
    } catch (startError) {
      notifyError(startError, startError instanceof Error ? startError.message : "Failed to enter organization");
    } finally {
      setPendingOrganizationId(null);
    }
  };

  if (isPending) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
  }

  if (error) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground">Failed to load organizations.</div>;
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Organizations" />
      <Card className="py-4">
        <CardHeader className="pb-2"><CardTitle className="text-base">Organizations</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Organization</TableHead><TableHead className="text-right">Support access</TableHead></TableRow></TableHeader>
            <TableBody>
              {organizations.length === 0 ? (
                <TableRow><TableCell colSpan={2} className="py-8 text-center text-muted-foreground">No organizations yet.</TableCell></TableRow>
              ) : organizations.map((organization) => (
                <TableRow key={organization._id}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2">
                      <Building2 className="size-4 text-muted-foreground" aria-hidden="true" />
                      {organization.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DisabledReasonTooltip
                      reason={
                        organization.isHomeOrganization
                          ? "You are already a member of this organization"
                          : undefined
                      }
                    >
                    <Button
                      size="sm"
                      disabled={pendingOrganizationId !== null || organization.isHomeOrganization}
                      onClick={() => void handleEnter(organization)}
                    >
                      {pendingOrganizationId === organization._id ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <LogIn className="size-4" aria-hidden="true" />
                      )}
                      {organization.isHomeOrganization ? "Your organization" : "Enter as admin"}
                    </Button>
                    </DisabledReasonTooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
